import { createContext, useContext, useState } from "react";
import { analyzeWebsite, getTransformationPlan } from "@/lib/api";
import LeadDialog from "@/components/LeadDialog";
import ConsultationDialog from "@/components/ConsultationDialog";
import { toast } from "sonner";

const AnalyzerContext = createContext(null);
export const useAnalyzer = () => useContext(AnalyzerContext);

export function AnalyzerProvider({ children }) {
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | loading | results
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);

  const scrollToResults = () =>
    setTimeout(() => document.getElementById("analysis-results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);

  const analyze = async (value) => {
    const target = (value ?? url).trim();
    if (!target) { toast.error("Enter a website URL to analyze."); return; }
    setUrl(target);
    setError(""); setPlan(null); setPhase("loading");
    try {
      const data = await analyzeWebsite(target);
      setAudit(data);
      setPhase("results");
      scrollToResults();
    } catch (err) {
      setError(err?.response?.data?.detail || "We couldn't reach that website. Check the URL and try again.");
      setPhase("idle");
      toast.error("Could not reach that website.");
    }
  };

  const generatePlan = async () => {
    if (!audit) return;
    setPlanLoading(true);
    try {
      const p = await getTransformationPlan(audit.id);
      setPlan(p);
      setTimeout(() => document.getElementById("transformation-plan")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch {
      toast.error("The AI plan couldn't be generated right now. Please try again.");
    } finally {
      setPlanLoading(false);
    }
  };

  const openLead = () => setLeadOpen(true);
  const openConsult = () => setConsultOpen(true);
  const reset = () => { setPhase("idle"); setAudit(null); setPlan(null); setUrl(""); setError(""); };

  const value = {
    url, setUrl, phase, audit, error, plan, planLoading,
    analyze, generatePlan, openLead, openConsult, reset,
  };

  return (
    <AnalyzerContext.Provider value={value}>
      {children}
      <LeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        context={{ website: audit?.domain, audit_id: audit?.id, website_score: audit?.overall_score, source: "Website Analyzer", service: "Website Transformation" }}
        onSuccess={generatePlan}
      />
      <ConsultationDialog
        open={consultOpen}
        onOpenChange={setConsultOpen}
        context={{ website: audit?.domain, service_interest: "Website Transformation" }}
      />
    </AnalyzerContext.Provider>
  );
}
